import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * SETUP:
 *   Mount the routes (one line in server.js):
 *     app.use("/api/worker-attendance", require("./routes/workerAttendanceRoutes"));
 *   Add the page route (e.g. App.js):
 *     <Route path="/worker-attendance-page" element={<WorkerAttendancePage />} />
 *
 * This page is manual: the supervisor looks at each worker's photo in the
 * table and taps Present or Absent for the selected date. No camera / face
 * recognition is used. Every mark is saved to the database immediately via
 * POST /api/worker-attendance/manual-mark.
 */

const API_BASE = "https://schoolcrm-fzcv.onrender.com/api";

const STATUS_STYLES = {
  Present: { bg: "#D1FAE5", color: "#059669", border: "#A7F3D0", dot: "#10B981" },
  Late: { bg: "#FEF3C7", color: "#D97706", border: "#FDE68A", dot: "#F59E0B" },
  Absent: { bg: "#FEE2E2", color: "#DC2626", border: "#FECACA", dot: "#EF4444" },
};

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function initials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function AttStatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.Absent;
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        padding: "4px 12px",
        borderRadius: 24,
        fontSize: 11,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot }} />
      {status}
    </span>
  );
}

function WorkerPhoto({ name, photo, size = 34 }) {
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        style={{ width: size, height: size, borderRadius: 10, objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  return (
    <div
      style={{
        width: size, height: size, borderRadius: 10,
        background: "linear-gradient(135deg, #0284C7, #0EA5E9)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.36, fontWeight: 700, color: "#FFFFFF", flexShrink: 0,
      }}
    >
      {initials(name)}
    </div>
  );
}

export default function WorkerAttendancePage() {
  const navigate = useNavigate();

  const [workers, setWorkers] = useState([]);
  const [dayRecords, setDayRecords] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [loadingTable, setLoadingTable] = useState(true);
  const [marking, setMarking] = useState({});

  const [view, setView] = useState("daily");
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFrom, setSummaryFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [summaryTo, setSummaryTo] = useState(todayStr());

  const [toast, setToast] = useState(null);

  const fetchWorkers = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/workers`);
      const data = await r.json();
      setWorkers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchWorkers error:", err);
    }
  }, []);

  const fetchDay = useCallback(async (d) => {
    setLoadingTable(true);
    try {
      const r = await fetch(`${API_BASE}/worker-attendance?date=${d}`);
      const data = await r.json();
      setDayRecords(data.records || []);
    } catch (err) {
      console.error("fetchDay error:", err);
      setDayRecords([]);
    } finally {
      setLoadingTable(false);
    }
  }, []);

  const fetchSummary = useCallback(async (from, to) => {
    setSummaryLoading(true);
    try {
      const r = await fetch(`${API_BASE}/worker-attendance/summary?from=${from}&to=${to}`);
      const data = await r.json();
      setSummary(data.summary || []);
    } catch (err) {
      console.error("fetchSummary error:", err);
      setSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    fetchDay(date);
  }, [date, fetchDay]);

  useEffect(() => {
    if (view === "summary") fetchSummary(summaryFrom, summaryTo);
  }, [view, summaryFrom, summaryTo, fetchSummary]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const markWorker = async (workerId, status) => {
    setMarking((p) => ({ ...p, [workerId]: true }));
    try {
      const r = await fetch(`${API_BASE}/worker-attendance/manual-mark`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workerId, date, status }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed to mark attendance");
      setToast({ type: "success", text: data.message });
      fetchDay(date);
    } catch (err) {
      console.error("markWorker error:", err);
      setToast({ type: "error", text: err.message });
    } finally {
      setMarking((p) => ({ ...p, [workerId]: false }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("username");
    navigate("/supervoicerlogin");
  };

  const handleBack = () => navigate("/workers");

  const photoByWorkerId = new Map(workers.map((w) => [String(w._id), w.photo]));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css');
        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #F1F5F9; font-family: 'Inter', sans-serif; }
        .att-btn { transition: all 0.2s ease; cursor: pointer; }
        .att-btn:hover { transform: translateY(-1px); filter: brightness(0.96); }
        .att-row:hover { background: #F8FAFC !important; }
        .mark-btn { transition: all 0.15s ease; cursor: pointer; }
        .mark-btn:hover { transform: translateY(-1px); }
        .mark-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        
        /* Mobile responsive styles */
        .attendance-table-header {
          display: grid;
          gap: 10px;
          padding: 10px 16px;
          background: #F8FAFC;
          border-bottom: 1px solid #E2E8F0;
        }
        
        .attendance-table-row {
          display: grid;
          gap: 10px;
          padding: 12px 16px;
          border-bottom: 1px solid #F1F5F9;
          align-items: center;
        }
        
        @media (min-width: 769px) {
          .attendance-table-header {
            grid-template-columns: 2fr 1fr 1fr 1fr 1.6fr;
          }
          .attendance-table-row {
            grid-template-columns: 2fr 1fr 1fr 1fr 1.6fr;
          }
        }
        
        @media (max-width: 768px) {
          .attendance-table-header {
            grid-template-columns: 1fr 1fr;
            display: none;
          }
          .attendance-table-row {
            grid-template-columns: 1fr;
            gap: 8px;
            padding: 16px;
            border: 1px solid #E2E8F0;
            border-radius: 12px;
            margin-bottom: 10px;
            background: #FFFFFF;
          }
          .mobile-label {
            display: inline-block;
            font-size: 10px;
            color: #94A3B8;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-right: 6px;
            min-width: 70px;
          }
        }
        
        @media (min-width: 769px) {
          .mobile-label {
            display: none;
          }
        }

        /* Summary table responsive */
        .summary-table-header {
          display: grid;
          gap: 10px;
          padding: 10px 16px;
          background: #F8FAFC;
          border-bottom: 1px solid #E2E8F0;
        }
        
        .summary-table-row {
          display: grid;
          gap: 10px;
          padding: 12px 16px;
          border-bottom: 1px solid #F1F5F9;
          align-items: center;
        }
        
        @media (min-width: 769px) {
          .summary-table-header {
            grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
          }
          .summary-table-row {
            grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
          }
        }
        
        @media (max-width: 768px) {
          .summary-table-header {
            display: none;
          }
          .summary-table-row {
            grid-template-columns: 1fr;
            gap: 6px;
            padding: 16px;
            border: 1px solid #E2E8F0;
            border-radius: 12px;
            margin-bottom: 10px;
            background: #FFFFFF;
          }
        }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#F1F5F9", fontFamily: "'Inter', sans-serif" }}>
        {/* HEADER */}
        <div
          style={{
            background: "linear-gradient(135deg, #0F172A, #1E293B, #334155)",
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderBottom: "2px solid #0284C7",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                background: "linear-gradient(135deg, #0284C7, #0EA5E9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                boxShadow: "0 4px 12px rgba(2,132,199,0.4)",
              }}
            >
              <i className="fas fa-user-check" style={{ color: "#FFFFFF" }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#FFFFFF" }}>
                Worker Attendance
              </div>
              <div
                style={{
                  fontSize: 8,
                  color: "#0284C7",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                Check photo, mark Present/Absent
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              onClick={handleBack}
              className="att-btn"
              style={{
                background: "#F8FAFC",
                color: "#475569",
                border: "1px solid #E2E8F0",
                padding: "5px 12px",
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              <i className="fas fa-arrow-left" style={{ marginRight: 4 }}></i>Back
            </button>
            <button
              onClick={() => setView(view === "summary" ? "daily" : "summary")}
              className="att-btn"
              style={{
                background: view === "summary" ? "linear-gradient(135deg, #0284C7, #0EA5E9)" : "#F8FAFC",
                color: view === "summary" ? "#FFFFFF" : "#475569",
                border: view === "summary" ? "none" : "1px solid #E2E8F0",
                padding: "5px 12px",
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              <i className="fas fa-chart-bar" style={{ marginRight: 4 }}></i>
              {view === "summary" ? "Daily" : "Summary"}
            </button>
            <button
              onClick={handleLogout}
              className="att-btn"
              style={{
                background: "#FEE2E2",
                color: "#DC2626",
                border: "1px solid #FECACA",
                padding: "5px 12px",
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              <i className="fas fa-arrow-right-from-bracket" style={{ marginRight: 4 }}></i>Logout
            </button>
          </div>
        </div>

        {/* TOAST */}
        {toast && (
          <div
            style={{
              position: "fixed",
              top: 76,
              right: 16,
              zIndex: 2000,
              animation: "fadeUp 0.3s ease",
              background: toast.type === "success" ? "#D1FAE5" : "#FEE2E2",
              color: toast.type === "success" ? "#059669" : "#DC2626",
              border: `1px solid ${toast.type === "success" ? "#A7F3D0" : "#FECACA"}`,
              padding: "10px 16px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600,
              boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
              maxWidth: 280,
            }}
          >
            {toast.text}
          </div>
        )}

        {view === "summary" ? (
          <div style={{ padding: "16px" }}>
            <div style={{ background: "#FFFFFF", borderRadius: 20, border: "1px solid #E2E8F0", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#0F172A" }}>
                  Summary <span style={{ fontWeight: 400, fontSize: 10, color: "#94A3B8" }}>({summary.length} workers)</span>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, color: "#64748B" }}>From</span>
                  <input type="date" value={summaryFrom} onChange={(e) => setSummaryFrom(e.target.value)} style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "4px 10px", fontSize: 11, maxWidth: 130 }} />
                  <span style={{ fontSize: 10, color: "#64748B" }}>To</span>
                  <input type="date" value={summaryTo} onChange={(e) => setSummaryTo(e.target.value)} style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "4px 10px", fontSize: 11, maxWidth: 130 }} />
                </div>
              </div>

              <div className="summary-table-header">
                {["Worker", "Role", "Present", "Late", "Leave/Absent", "Total"].map((h) => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1px" }}>{h}</div>
                ))}
              </div>

              {summaryLoading ? (
                <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
                  <i className="fas fa-spinner fa-pulse" style={{ marginRight: 8 }}></i>Loading...
                </div>
              ) : summary.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>No data for this range</div>
              ) : (
                summary.map((w, idx) => (
                  <div key={w.workerId} className="summary-table-row">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <WorkerPhoto name={w.name} photo={photoByWorkerId.get(String(w.workerId))} size={32} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{w.name}</span>
                    </div>
                    <div><span className="mobile-label">Role:</span><span style={{ fontSize: 11, color: "#475569" }}>{w.role}</span></div>
                    <div><span className="mobile-label">Present:</span><span style={{ fontSize: 12, fontWeight: 700, color: "#059669" }}>{w.presentCount}</span></div>
                    <div><span className="mobile-label">Late:</span><span style={{ fontSize: 12, fontWeight: 700, color: "#D97706" }}>{w.lateCount}</span></div>
                    <div><span className="mobile-label">Leave/Absent:</span><span style={{ fontSize: 12, fontWeight: 700, color: "#DC2626" }}>{w.leaveCount}</span></div>
                    <div><span className="mobile-label">Total:</span><span style={{ fontSize: 11, color: "#94A3B8" }}>{w.totalDays}</span></div>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: 12, fontSize: 10, color: "#94A3B8" }}>
              <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
              "Leave / Absent" = calendar days with no attendance mark recorded.
            </div>
          </div>
        ) : (
          <div style={{ padding: "16px" }}>
            <div style={{ background: "#FFFFFF", borderRadius: 20, border: "1px solid #E2E8F0", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#0F172A" }}>
                  Attendance <span style={{ fontWeight: 400, fontSize: 10, color: "#94A3B8" }}>({dayRecords.length} workers)</span>
                </div>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "4px 10px", fontSize: 11, maxWidth: 150 }}
                />
              </div>

              <div className="attendance-table-header">
                {["Worker", "Role", "Check-in", "Status", "Mark Attendance"].map((h) => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1px" }}>{h}</div>
                ))}
              </div>

              {loadingTable ? (
                <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
                  <i className="fas fa-spinner fa-pulse" style={{ marginRight: 8 }}></i>Loading...
                </div>
              ) : dayRecords.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>No workers found</div>
              ) : (
                dayRecords.map((rec) => {
                  const isPresent = rec.attendanceStatus === "Present" || rec.attendanceStatus === "Late";
                  const isAbsent = rec.attendanceStatus === "Absent";
                  const busy = !!marking[rec.workerId];
                  return (
                    <div key={rec.workerId} className="attendance-table-row">
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <WorkerPhoto name={rec.name} photo={photoByWorkerId.get(String(rec.workerId))} size={32} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{rec.name}</span>
                      </div>
                      <div><span className="mobile-label">Role:</span><span style={{ fontSize: 11, color: "#475569" }}>{rec.role}</span></div>
                      <div><span className="mobile-label">Check-in:</span><span style={{ fontSize: 11, color: "#475569" }}>{rec.checkInTime && rec.checkInTime !== "—" ? rec.checkInTime : "—"}</span></div>
                      <div><span className="mobile-label">Status:</span><AttStatusBadge status={rec.attendanceStatus} /></div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                        <button
                          className="mark-btn"
                          disabled={busy}
                          onClick={() => markWorker(rec.workerId, "Present")}
                          style={{
                            background: isPresent ? "#059669" : "#F0FDF4",
                            color: isPresent ? "#FFFFFF" : "#059669",
                            border: "1px solid #A7F3D0",
                            padding: "5px 10px",
                            borderRadius: 8,
                            fontSize: 10,
                            fontWeight: 700,
                            flex: "1 1 auto",
                            minWidth: 70,
                          }}
                        >
                          <i className="fas fa-check" style={{ marginRight: 4 }}></i>Present
                        </button>
                        <button
                          className="mark-btn"
                          disabled={busy}
                          onClick={() => markWorker(rec.workerId, "Absent")}
                          style={{
                            background: isAbsent ? "#DC2626" : "#FEF2F2",
                            color: isAbsent ? "#FFFFFF" : "#DC2626",
                            border: "1px solid #FECACA",
                            padding: "5px 10px",
                            borderRadius: 8,
                            fontSize: 10,
                            fontWeight: 700,
                            flex: "1 1 auto",
                            minWidth: 70,
                          }}
                        >
                          <i className="fas fa-times" style={{ marginRight: 4 }}></i>Absent
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div style={{ marginTop: 12, fontSize: 10, color: "#94A3B8" }}>
              <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
              Look at each worker's photo to confirm identity, then mark Present or Absent for {date}.
            </div>
          </div>
        )}
      </div>
    </>
  );
}